import React from "react";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { TreeItem } from "@mui/x-tree-view";
import ModuleIconBadge from "../../../../ModuleIconBadge";
import {
  WBS_GROUPS,
  isSoftwareWbs,
  treeGroupKey,
  wbsGroupNodeId,
} from "../utils/wbsGroups";
import { hasDrawings } from "../utils/treeLabels";
import { useTreeViewContext } from "../TreeViewContext";
import { plantNodeId, sectionNodeId } from "../utils/treeNodeIds";
import {
  PROCUREMENT_CATEGORIES,
  PLACEHOLDER_SECTIONS,
} from "../utils/treeConstants";
import { AddActionIcon, AddActionLabel } from "./AddAction";
import { WbsNode } from "./WbsNode";
import { TreeNodeIcon } from "./TreeNodeIcon";
import { ProcurementCategoryNode, SectionItemNode } from "./ProcurementNodes";
import { DesignAutomationItemNode } from "./DesignAutomationNodes";

// The Add WBS button for a plant. Rendered either on the plant's own row
// or, once the list is focused on that plant, up in the header next to the
// breadcrumb - identical either way.
const PlantAddWbsButton = ({ plant }) => {
  const { pickerMode, wbsOperations } = useTreeViewContext();
  if (pickerMode) return null;

  return (
    <IconButton
      size="small"
      title="Add a WBS to this plant"
      onClick={(event) => {
        event.stopPropagation();
        wbsOperations.openAddWbs(plant);
      }}
      sx={{ p: 0.4, borderRadius: 1 }}
    >
      <AddActionIcon color="warning.main" />
      <AddActionLabel>Add WBS</AddActionLabel>
    </IconButton>
  );
};

// A plant's WBS bucketed into one folder per discipline, labeled by the
// discipline's name (Pressure Vessel / Piping / …), in WBS_GROUPS order.
// Empty disciplines are dropped rather than shown as empty folders.
const WbsDisciplineGroups = ({ plant, visibleWbs }) => {
  const byGroup = new Map();
  visibleWbs.forEach((wbs) => {
    const key = treeGroupKey(wbs);
    if (!byGroup.has(key)) byGroup.set(key, []);
    byGroup.get(key).push(wbs);
  });

  return (
    <>
      {WBS_GROUPS.filter((group) => byGroup.has(group.key)).map((group) => {
        const groupWbs = byGroup.get(group.key);
        const groupId = wbsGroupNodeId(plant.id, group.key);

        return (
          <TreeItem
            key={groupId}
            nodeId={groupId}
            icon={<TreeNodeIcon moduleKey={group.key} />}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                >
                  {group.label}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  ({groupWbs.length})
                </Typography>
              </Box>
            }
          >
            {groupWbs.map((wbs) => (
              <WbsNode key={wbs.id} plant={plant} wbs={wbs} />
            ))}
          </TreeItem>
        );
      })}
    </>
  );
};

const PlantWbsChildren = ({ plant }) => {
  const { treeData, pickerMode } = useTreeViewContext();
  const wbsList = treeData.wbsByPlant[plant.id];
  const id = plantNodeId(plant.id);

  if (wbsList === "loading") {
    return (
      <TreeItem
        key={`${id}-loading`}
        nodeId={`${id}-loading`}
        label={
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}
          >
            <CircularProgress size={12} />
            <Typography variant="caption" color="text.secondary">
              Loading WBS…
            </Typography>
          </Box>
        }
      />
    );
  }
  if (Array.isArray(wbsList)) {
    // Only WBS of a type the website creates are listed. Legacy and
    // unrecognised types are what "Other" used to collect (see isSoftwareWbs)
    // - nothing in the tree opens them, so listing them only offered rows
    // that lead nowhere.
    //
    // Picker mode keeps them: it is the Validation page's Compare asking for
    // a side, and a drawing filed under one of these WBS is still a drawing
    // it can be given. Only those carrying drawings, though - the rest have
    // nothing to pick either way.
    const websiteWbs = pickerMode
      ? wbsList.filter(
          (wbs) =>
            !isSoftwareWbs(wbs) || hasDrawings(wbs, treeData.drawingsByWbs),
        )
      : wbsList.filter((wbs) => !isSoftwareWbs(wbs));
    const visibleWbs = websiteWbs.filter((wbs) =>
      treeData.isWbsVisible(plant, wbs),
    );
    if (visibleWbs.length > 0) {
      return <WbsDisciplineGroups plant={plant} visibleWbs={visibleWbs} />;
    }
    return (
      <TreeItem
        key={`${id}-empty`}
        nodeId={`${id}-empty`}
        label={
          <Typography variant="caption" color="text.secondary">
            {websiteWbs.length === 0 ? "No WBS" : "No matches"}
          </Typography>
        }
      />
    );
  }
  return (
    <TreeItem
      key={`${id}-placeholder`}
      nodeId={`${id}-placeholder`}
      label=""
    />
  );
};

// A section heading's label: the section's module badge beside its caption,
// so each section keeps the colour it has everywhere else in the app.
const SectionLabel = ({ sectionKey, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <ModuleIconBadge moduleKey={sectionKey} size="xs" />
    <span>{label}</span>
  </Box>
);

export const PlantNode = ({ plant }) => {
  const { activePlantId, pickerMode, nodeTargetRef, plantOperations } =
    useTreeViewContext();
  const { openPlantMenu } = plantOperations;

  const id = plantNodeId(plant.id);
  nodeTargetRef.current[id] = { kind: "plant", plant };

  return (
    <TreeItem
      key={id}
      nodeId={id}
      icon={<ModuleIconBadge moduleKey="plant" size="sm" />}
      label={
        <Box
          onContextMenu={
            pickerMode ? undefined : (event) => openPlantMenu(event, plant)
          }
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Typography
            variant="body2"
            noWrap
            title={plant.name}
            sx={{
              flex: 1,
              minWidth: 0,
              fontWeight: plant.id === activePlantId ? 700 : 500,
            }}
          >
            {plant.name || "Untitled"}
          </Typography>
          <PlantAddWbsButton plant={plant} />
        </Box>
      }
    >
      {/* Every section a project holds, each one a folder of its own rather
          than a caption labelling the rows that follow it - so it opens and
          closes on a click like the rest of the tree, and a project comes up
          as a short list of what it holds instead of everything at once.

          The WBS are the Estimation section: that is what a WBS is, and it
          leaves them sitting beside Procurement and the rest rather than
          above them unlabelled. */}
      <TreeItem
        key={sectionNodeId(plant.id, "estimation")}
        nodeId={sectionNodeId(plant.id, "estimation")}
        className="tree-section-label tree-section-label--estimation"
        label={<SectionLabel sectionKey="estimation" label="Estimation" />}
      >
        <PlantWbsChildren plant={plant} />
      </TreeItem>
      <TreeItem
        key={sectionNodeId(plant.id, "procurement")}
        nodeId={sectionNodeId(plant.id, "procurement")}
        className="tree-section-label tree-section-label--procurement"
        label={<SectionLabel sectionKey="procurement" label="Procurement" />}
      >
        {PROCUREMENT_CATEGORIES.map((category) => (
          <ProcurementCategoryNode
            key={category.key}
            plant={plant}
            category={category}
          />
        ))}
      </TreeItem>
      {PLACEHOLDER_SECTIONS.map((section) => {
        // Design Automation's rows hold the WBS of their discipline; every
        // other section's are plain leaves.
        const ItemNode =
          section.key === "design-automation"
            ? DesignAutomationItemNode
            : SectionItemNode;

        return (
          <TreeItem
            key={sectionNodeId(plant.id, section.key)}
            nodeId={sectionNodeId(plant.id, section.key)}
            className={`tree-section-label tree-section-label--${section.key}`}
            label={<SectionLabel sectionKey={section.key} label={section.label} />}
          >
            {section.items.map((item) => (
              <ItemNode
                key={item.key}
                plant={plant}
                sectionKey={section.key}
                item={item}
              />
            ))}
          </TreeItem>
        );
      })}
    </TreeItem>
  );
};
